import { Box, Button, Skeleton, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { AmexLogo, DinersLogo, DiscoverLogo, FourDots, GenericCardLogo, MasterCardLogo, VisaCardLogo, cardSurface } from '../../../../shared/lib/assets';
import { Alert } from '../../../../shared/lib/common/Alert';
import { CardBrands, GRAPHQL_QUERY_POLICY, LABEL_NOT_AVAILABLE, LABEL_PAYMENT_METHOD } from '../../../../shared/lib/constants';
import { SECONDARY_FONT, Ubuntu } from '../../../../shared/lib/constants/fonts';
import palette from '../../../../shared/lib/theme/palette';
import { billingWrapper } from '../../../../shared/lib/theme/styleConstants';
import { useAppContext } from '../../app/contexts/AppContext';
import { CardData } from '../../app/interfaceTypes';
import { useFetchCardDetailsLazyQuery } from '../../generated';
import { EditPaymentMethod } from '../menus/EditPaymentMethod';
import StripeChangeCardModal from './StripeChangeCardModal';
import { ActionType } from '../../app/reducer/AppContextReducer';

export const PaymentMethod = () => {
  const { blackTwo, white } = palette;
  const { currentUser, dispatch } = useAppContext();
  const { isSubscribed } = currentUser ?? {};
  const { customerStripeId } = isSubscribed ?? {};
  const [cardData, setCardData] = useState<CardData>({ name: LABEL_NOT_AVAILABLE, last4: '0000', expiry: '00/00' });
  const [currentBrand, setCurrentBrand] = useState<string>('');

  const onAddCard = () => {
    dispatch({ type: ActionType.SET_IS_CHANGE_PLAN, isChangePlan: true });
  };

  const brandLogoMap: Record<string, () => JSX.Element> = {
    [CardBrands.VISA]: VisaCardLogo,
    [CardBrands.MASTERCARD]: MasterCardLogo,
    [CardBrands.American_Express]: AmexLogo,
    [CardBrands.DISCOVER]: DiscoverLogo,
    [CardBrands.DINERSCLUB]: DinersLogo,
    [CardBrands.GENERIC]: GenericCardLogo
  };

  const renderCardLogo = (brand: string): JSX.Element => {
    const logoFn = brandLogoMap[brand.toLowerCase()] || GenericCardLogo;
    return logoFn();
  };

  const [fetchCardDetails, { loading, refetch }] = useFetchCardDetailsLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,

    onError({ message }) {
      Alert.error(message);
    },

    onCompleted(data) {
      const { fetchCardDetails } = data;
      if (fetchCardDetails) {
        const { exp_month, exp_year: expiryYear, last4: cardLast4, name: cardName, brand } = fetchCardDetails;
        if (cardName) {
          setCardData({ name: cardName, last4: cardLast4, expiry: String(exp_month).padStart(2, '0') + '/' + expiryYear });
        } else {
          setCardData({ name: LABEL_NOT_AVAILABLE, last4: cardLast4, expiry: String(exp_month).padStart(2, '0') + '/' + expiryYear });
        }
        setCurrentBrand(brand ?? '');
      }
    }
  });

  const fetchCard = async () => {
    customerStripeId &&
      (await fetchCardDetails({
        variables: {
          fetchCardDetailsInput: customerStripeId
        }
      }));
  };

  useEffect(() => {
    (async () => await fetchCard())();
    // eslint-disable-next-line
  }, [customerStripeId]);

  return (
    <Box sx={billingWrapper}>
      <Typography variant="h2" sx={{ textTransform: 'uppercase', color: blackTwo, pb: '16px' }}>
        {LABEL_PAYMENT_METHOD}
      </Typography>
      {cardData.expiry !== '00/00' ? (
        <>
          <EditPaymentMethod />

          <Box position="relative" height="185px" maxWidth="335px" width="100%" margin="auto">
            <Box width="100%" height="100%">
              {loading ? <Skeleton animation="wave" variant="rounded" width={336} height={185} /> : cardSurface()}
            </Box>

            <Box position="absolute" height="100%" width="100%" top={0} sx={{ opacity: loading ? 0 : 100 }}>
              <Box display="flex" flexDirection="column" justifyContent="space-between" padding="16px 10px" height="100%">
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" sx={{ fontWeight: '700', color: white, fontFamily: Ubuntu }}>
                    Credit
                  </Typography>

                  {renderCardLogo(currentBrand)}
                </Box>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: '700', color: white, pb: '5px', fontFamily: SECONDARY_FONT }}>
                    {cardData.name}
                  </Typography>

                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle2" sx={{ color: white, fontFamily: SECONDARY_FONT }}>
                      {FourDots()} {cardData.last4}
                    </Typography>

                    <Typography variant="subtitle2" sx={{ fontWeight: '700', color: white, fontFamily: SECONDARY_FONT }}>
                      EXP : {cardData.expiry}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </>
      ) : (
        <Box display="flex" justifyContent={'center'} sx={{ my: 4 }}>
          {loading ? (
            <Box>
              <Skeleton animation="wave" variant="rounded" width={336} height={185} />
            </Box>
          ) : (
            <Button variant="contained" onClick={onAddCard}>
              Add Card
            </Button>
          )}
        </Box>
      )}
      <StripeChangeCardModal setCardData={setCardData} refetch={refetch} />
    </Box>
  );
};
